"use client";

import * as React from "react";
import { useRouter } from "next/navigation";
import { Loader2, Plus } from "lucide-react";
import { Modal } from "@/components/ui/modal";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAsyncAction } from "@/lib/use-async-action";
import type { Property } from "@/lib/types";

export function AddReservationModal({
  properties,
  trigger,
}: {
  properties: Pick<Property, "id" | "name">[];
  trigger?: React.ReactNode;
}) {
  const router = useRouter();
  const [open, setOpen] = React.useState(false);
  const [propertyId, setPropertyId] = React.useState("");
  const [checkin, setCheckin] = React.useState("");
  const [checkout, setCheckout] = React.useState("");
  const [phone, setPhone] = React.useState("");
  const [url, setUrl] = React.useState("");
  const [error, setError] = React.useState<string | null>(null);

  const action = useAsyncAction(async () => {
    setError(null);
    const res = await fetch("/api/reservations", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        property_id: propertyId,
        checkin_date: checkin,
        checkout_date: checkout,
        guest_phone_last4: phone || undefined,
        reservation_url: url || undefined,
      }),
    });
    if (!res.ok) {
      const data = await res.json().catch(() => ({}));
      throw new Error(data.error ?? "Failed to add reservation");
    }
    setPropertyId("");
    setCheckin("");
    setCheckout("");
    setPhone("");
    setUrl("");
    setOpen(false);
    router.refresh();
  });

  const canSubmit = propertyId && checkin && checkout && !action.busy;

  return (
    <Modal
      open={open}
      onOpenChange={setOpen}
      trigger={
        trigger ?? (
          <Button size="sm" variant="outline">
            <Plus className="size-4" />
            <span className="hidden sm:inline">Add reservation</span>
          </Button>
        )
      }
      title="Add reservation"
      description="Manually create a reservation that isn't coming from the iCal feed."
      size="sm"
      footer={
        <>
          <Button variant="outline" onClick={() => setOpen(false)} disabled={action.busy}>Cancel</Button>
          <Button
            disabled={!canSubmit}
            onClick={() => action.run().catch((e) => setError((e as Error).message))}
          >
            {action.busy && <Loader2 className="size-4 animate-spin" />}
            Add
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        <div className="space-y-2">
          <Label htmlFor="property">Property</Label>
          <Select value={propertyId} onValueChange={setPropertyId}>
            <SelectTrigger id="property"><SelectValue placeholder="Select a property" /></SelectTrigger>
            <SelectContent>
              {properties.map((p) => (
                <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="checkin">Check-in</Label>
            <Input id="checkin" type="date" value={checkin} onChange={(e) => setCheckin(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="checkout">Checkout</Label>
            <Input id="checkout" type="date" value={checkout} onChange={(e) => setCheckout(e.target.value)} />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="phone">Guest phone (last 4) <span className="text-muted-foreground text-xs">— optional</span></Label>
          <Input id="phone" inputMode="numeric" placeholder="1234" value={phone} onChange={(e) => setPhone(e.target.value)} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="url">Reservation URL <span className="text-muted-foreground text-xs">— optional</span></Label>
          <Input id="url" type="url" placeholder="https://airbnb.com/..." value={url} onChange={(e) => setUrl(e.target.value)} />
        </div>
        {error && <p className="text-sm text-destructive">{error}</p>}
      </div>
    </Modal>
  );
}
